import { GridReadyEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { takeUntil } from "rxjs/operators";
import { FeatureKind } from "../model/entities/entity-model";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { EditFeatureKindDialogComponent } from "./edit-feature-kind-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";

/**
 * Maintains the list of feature types.
 *
 * A feature type says what a Feature MEANS rather than what it is called, and the manufacturer
 * feed reads it to decide which features a product type must carry. There are four today and
 * they are rarely touched, which is exactly why they needed a screen: until now they were only
 * reachable by running a script.
 *
 * THE LIST IS HALF OF A PAIR. Every row mirrors a member of FeatureKindEnum, in C# and in
 * TypeScript, and the special cases compare against the enum rather than against these names.
 *
 * A row added here is immediately real to the generic paths: a Feature can be given the type,
 * a manufacturer can require it, and product-type.component enforces that requirement without
 * knowing what the type means. What a new row can never reach is the handling reserved for
 * Size and Color - the product builder's size/color pairing, getColorFeatureId, repoint-color,
 * the feed's size-coverage check. Those read the enum, so the type stays invisible to them
 * until a matching member exists in both languages.
 *
 * Renaming a row is always safe.
 */
@Component({
  selector: "app-feature-kind",
  templateUrl: "./feature-kind.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FeatureKindComponent extends DomainBaseComponent implements OnInit {
  fkGridOptions: GridOptionsExt;
  selectedFeatureKind: FeatureKind;
  featureKinds: FeatureKind[];

  constructor(protected domainService: DomainService, protected route: ActivatedRoute) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.fkGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onFkGridReady,
      onRowSelected: AgFns.handleRowSelected((e: FeatureKind) => (this.selectedFeatureKind = e)),
    });
    AgFns.captureGridRouteParams(this.fkGridOptions, this.route, "id");

    this.featureKinds = await this.dbQueryService.getFeatureKinds();

    this.isPageReady = true;
  }

  /**
   * Deliberately does NOT clear FeatureKind from the entity manager, unlike the sibling
   * reference pages. These rows are loaded once at login as a lookup and read from the cache by
   * the Feature dialog and the product builder; dropping them on the way out would leave those
   * screens with an empty type list until the next full reload.
   */
  async canDeactivate() {
    return await super.canDeactivate();
  }

  onFkGridReady(event: GridReadyEvent) {
    const colDefs = [
      this.productAdminAllow({
        ...AgFns.createButtonProps("", this.featureKindEdit.bind(this), {
          label: "Edit",
        }),
        maxWidth: 60,
        minWidth: 60,
      }),
      { headerName: "Id", field: "id", type: "leftAligned", ...AgFns.fixWidth(80) },
      // flexWidth, not a bare column: everything else here is fixWidth and therefore suppressed
      // from sizeColumnsToFit, so Name is the only column that can absorb the remainder.
      { headerName: "Name", field: "name", filter: "agTextColumnFilter", ...AgFns.flexWidth(200) },
      {
        headerName: "Features",
        valueGetter: (params) => (params.data?.features?.length ?? 0),
        type: "leftAligned",
        ...AgFns.fixWidth(110),
      },
      {
        ...UniAgFns.getEntityDeleteColDef(this.onDelete.bind(this), {
          canDisplay: () => true,
        }),
        // Wider than the 60 the helper gives, so the trash icon is not against the grid edge.
        ...AgFns.fixWidth(80),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "id", sort: "asc" }]);
  }

  productAdminAllow(x: any) {
    return this.user.isProductAdmin ? x : {};
  }

  // -----------------------------------------------------

  @wrapBusy
  async onDelete(fk: FeatureKind) {
    // Two things point at a feature type and both block the delete. A feature would be left
    // with a type that does not exist; a manufacturer requirement would be left demanding one.
    if (fk.features?.length > 0) {
      await this.dialogService.okDialog(
        "Cannot Delete",
        `${fk.features.length} feature(s) use this feature type. Change those features first.`
      );
      return;
    }
    if (fk.manufacturerFeatureKindMaps?.length > 0) {
      await this.dialogService.okDialog(
        "Cannot Delete",
        `${fk.manufacturerFeatureKindMaps.length} manufacturer(s) require this feature type. Remove the requirement first.`
      );
      return;
    }

    const ok = await this.dialogService.yesNoDialog("Delete this Feature Type?", "Are you sure?");
    if (!ok) return;

    fk.entityAspect.setDeleted();
    const sr = await this.dbSaveService.saveChanges();
    if (sr.httpResponse.statusText == "OK") {
      this.toastr.success("The system deleted the feature type.");
      this.featureKinds = await this.dbQueryService.getFeatureKinds();
      AgFns.refreshGrid(this.fkGridOptions, this.featureKinds);
    } else {
      this.toastr.error("Feature Type delete failed", "Unable to Delete");
    }
  }

  async featureKindEdit(featureKind?: FeatureKind) {
    const r = await DialogBaseComponent.open(EditFeatureKindDialogComponent, this, {
      featureKind: featureKind,
      // Two fields and a note. The 60vw/60vh default left most of the dialog empty.
      dialogWidth: "calc(30vw)",
      dialogHeight: "calc(24vh)",
    });
    if (r) {
      this.featureKinds = await this.dbQueryService.getFeatureKinds();
      AgFns.refreshGrid(this.fkGridOptions, this.featureKinds);
    }
  }
}
